<template>
  <div class="conversation-detail">
    <LoadingIndicator centered v-if="loading && !conversation" />
    <div v-if="conversation" class="conversation-container">
      <div class="conversation-header">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h4 class="mb-1">
              Conversation #{{ conversation.id }}
            </h4>
            <p class="text-muted mb-0">
              <ConversationStatusBadge :status="conversation.status" />
              <span class="ml-2">
                <i class="fa fa-clock"></i>
                Started {{ formatTimeAgo(conversation.startedAt) }}
              </span>
            </p>
          </div>
          <div class="action-buttons">
            <button
              v-if="canTakeOver"
              class="btn mr-2 purple"
              @click="takeOver"
              :disabled="takingOver"
            >
              <i class="fa" :class="takingOver ? 'fa-spinner fa-spin' : 'fa-user-shield'"></i>
              {{ takingOver ? 'Taking Over...' : 'Take Over' }}
            </button>
            <button
              v-if="canClose"
              class="btn btn-secondary"
              @click="confirmClose"
            >
              <i class="fa fa-times-circle"></i>
              Close Conversation
            </button>
          </div>
        </div>
      </div>

      <div class="conversation-content">
        <div class="messages-section">
          <div ref="messagesContainer" class="messages-container">
            <div v-if="messages.length === 0" class="empty-messages">
              <i class="fa fa-comments fa-3x text-muted"></i>
              <p class="mt-3 text-muted">No messages yet</p>
            </div>
            <MessageBubble
              v-for="message in messages"
              :key="message.id"
              :message="message"
              :agent="conversation.agent"
            />
          </div>

          <MessageInput
            :enabled="canSendMessages"
            :conversation-id="conversationId"
            :disabled-message="inputDisabledMessage"
            @send="sendMessage"
          />
        </div>

        <div class="context-section">
          <ContextSidebar :conversation="conversation"/>
        </div>
      </div>
    </div>

    <EmptyState v-if="!loading && !conversation" message="Conversation not found" />

    <b-modal
      ref="closeConfirmModal"
      title="Close Conversation"
      @ok="closeConversation"
      ok-title="Close Conversation"
      ok-variant="danger"
      cancel-title="Cancel"
    >
      <p>Are you sure you want to close this conversation?</p>
      <p class="text-muted mb-0">
        <i class="fa fa-info-circle"></i>
        This action cannot be undone. The customer will no longer receive responses.
      </p>
    </b-modal>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import { mapState } from 'vuex';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import EmptyState from '@/components/shared/EmptyState';
import MessageBubble from '@/components/shared/MessageBubble';
import ContextSidebar from '@/components/shared/ContextSidebar';
import MessageInput from '@/components/shared/MessageInput';
import ConversationStatusBadge from '@/components/shared/ConversationStatusBadge';
import moment from 'moment';
import { objectToCamelCase } from '@/api/caseHelpers';

export default {
  name: 'ConversationDetail',
  components: {
    LoadingIndicator,
    EmptyState,
    MessageBubble,
    ContextSidebar,
    MessageInput,
    ConversationStatusBadge,
  },
  data() {
    return {
      conversation: null,
      messages: [],
      loading: false,
      takingOver: false,
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    conversationId() {
      return parseInt(this.$route.params.id);
    },
    canTakeOver() {
      return this.conversation && this.conversation.status === 'active' && !this.conversation.agent;
    },
    canClose() {
      return this.conversation && this.conversation.status !== 'closed';
    },
    canSendMessages() {
      if (!this.conversation) return false;
      if (this.conversation.status !== 'taken_over') return false;

      return !!this.conversation.agent && this.isCurrentUserAgent;
    },
    isCurrentUserAgent() {
      if (!this.conversation?.agent || !this.user) return false;
      return (this.conversation.agent.id === this.user.id) || !this.conversation.agent;
    },
    inputDisabledMessage() {
      if (!this.conversation) return 'Loading...';
      if (this.conversation.status === 'closed') return 'This conversation is closed';
      if (this.conversation.status === 'active') return 'Take over the conversation to send messages';
      if (this.conversation.agent && !this.isCurrentUserAgent) {
        return `This conversation is being handled by ${this.conversation.agent.name}`;
      }
      return 'You cannot send messages in this conversation';
    }
  },
  methods: {
    async fetchConversation() {
      if (this.loading) return;

        this.loading = true;
        let response = await ApiClient.chatbot.conversations.get({ id: this.conversationId });
        this.loading = false;

        if (response.status === 200) {
          response = JSON.parse(JSON.stringify(response))
          this.conversation = response.data.data;
          this.messages = response?.data?.data?.messages || [];

          this.$nextTick(() => {
            this.scrollToBottom();
          });

        }
        else{
          this.conversation = null
          this.messages = []
        }
    },
    async takeOver() {
      if (this.takingOver) return;

      this.takingOver = true;
      const response = await ApiClient.chatbot.conversations.takeover({ id: this.conversationId });
      this.takingOver = false;

      if (response.status === 200) {
        const systemMessage = {
          id: Date.now(),
          sender_type: 'system',
          content: 'Agent has joined the conversation',
          created_at: new Date().toISOString(),
        };
        this.messages.push(systemMessage);
        await this.fetchConversation();

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    async sendMessage(content) {
        await ApiClient.chatbot.conversations.messages.post({
          conversationId: this.conversationId,
          body: { message: content },
        });
    },

    confirmClose() {
      this.$refs.closeConfirmModal.show();
    },
    async closeConversation() {
      const response = await ApiClient.chatbot.conversations.close({ id: this.conversationId });

      if (response.status === 200) {
        this.conversation.status = 'closed';

        setTimeout(() => {
          this.$router.push({ name: 'ChatbotConversationList' });
        }, 1500);
      }
    },
    setupWebSocket() {
      if (!this.conversation) {
        console.warn('[ConversationDetail] Cannot setup WebSocket: conversation is null');
        return;
      }

      const sessionId = this.conversation.sessionId || this.conversation.session_id;

      if (!sessionId) {
        console.warn('[ConversationDetail] Cannot setup WebSocket: sessionId not found on conversation object', this.conversation);
        return;
      }

      const channelName = `chatbot.conversations.${sessionId}`;

      const specificChannel = this.$echo.private(channelName);

      specificChannel
        .listen('.ChatbotMessageReceived', e => {
          this.handleWebSocketMessage(e);
        })
        .error(error => {
          console.error('[ConversationDetail] ❌ WebSocket error on specific channel:', channelName, error);
        });

      const generalChannel = this.$echo.private('chatbot.conversations');

      generalChannel
        .listen('.ChatbotMessageReceived', e => {
          if (e.conversation && e.conversation.id === this.conversationId) {
            this.handleWebSocketMessage(e);
          }
        })
        .error(error => {
          console.error('[ConversationDetail] ❌ WebSocket error on general channel:', error);
        });

    },
    cleanupWebSocket() {
      if (this.conversation) {
        const sessionId = this.conversation.sessionId || this.conversation.session_id;
        if (sessionId) {
          const channelName = `chatbot.conversations.${sessionId}`;
          this.$echo.leave(channelName);
        }
      }
      this.$echo.leave('chatbot.conversations');
    },
    handleWebSocketMessage(event) {
      const data = objectToCamelCase(event);
      const { message, conversation } = data;

      if (!message) {
        console.warn('[ConversationDetail] Message is undefined in WebSocket event');
        return;
      }

      const existingMessage = this.messages.find(m => m.id === message.id);
      if (existingMessage) {
        return;
      }

      this.messages.push(message);

      if (conversation && conversation.status) {
        this.conversation.status = conversation.status;
      }

      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },
    formatTimeAgo(dateTime) {
      if (!dateTime) return '';
      return moment(dateTime).fromNow();
    },
  },
  created() {
    this.fetchConversation().then(() => {
      if (this.conversation?.sessionId) {
        this.setupWebSocket();
      }
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },
  beforeDestroy() {
    this.cleanupWebSocket();
  },
};
</script>

<style lang="scss" scoped>
.conversation-detail {
  display: flex;
  flex-direction: column;
}

.conversation-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.conversation-header {
  padding-bottom: 1rem;
  flex-shrink: 0;

  h4 {
    margin: 0;
    display: flex;
    align-items: center;

    .btn-link {
      padding: 0;
      margin-right: 0.5rem;
      color: #6c757d;

      &:hover {
        color: #007bff;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 0.5rem;
  }
}

.conversation-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.messages-section {
  flex: 1;
  height: calc(100vh - 225px);
  display: flex;
  flex-direction: column;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;

  .empty-messages {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #6c757d;
  }

  .typing-indicator {
    text-align: center;
    padding: 0.5rem;
    color: #6c757d;
    font-size: 0.875rem;

    i {
      margin-right: 0.5rem;
    }
  }
}

.context-section {
  width: 436px;
  padding-left: 2rem;
}

@media (max-width: 992px) {
  .conversation-content {
    flex-direction: column;
  }

  .context-section {
    width: 100%;
    border-right: none;
  }
}

.purple{
  background-color: #9039B6;
  color: white;
}
</style>
